/* ==========================================================================
   Superboba Bundle Funnel LP — JavaScript
   Two-step bundle builder: pricing tiers → flavor selection → checkout
   An optional toppings phase sits at the end of step 2 (toppings_product).
   ========================================================================== */

(function () {
  'use strict';

  /* ========================================================================
     STATE
     ======================================================================== */
  const state = {
    currentStep: 1,
    purchaseMode: 'sub',     // 'sub' (subscription tiers) | 'once' (try-once, no selling plan)
    selectedTier: 0,         // index of selected pricing tier (default: first/best deal)
    tierQuantity: 4,         // total packs needed for this tier
    flavors: {},             // { variantId: quantity }
    totalSelected: 0,        // sum of all flavor quantities
    toppings: 0,             // toppings bags (one-time add-on)
    toppingsPhase: false,    // step 2 shows the toppings list, not the flavors
    carouselIndex: 0,
    carouselCount: 0,
  };

  /* ========================================================================
     SUBSCRIPTION SELLING PLAN MAPPING
     1-2 bags → 30-day plan, 3-4 bags → 60-day plan, 5+ bags → longer plan
     ======================================================================== */
  // A section can point at another selling plan group with
  // data-selling-plans="30:<id>, 60:<id>, 90:<id>" (days:plan id). The
  // bag count picks the frequency. No attribute keeps the store default.
  var sellingPlanMap = null;
  function getSellingPlanMap() {
    if (sellingPlanMap) return sellingPlanMap;
    sellingPlanMap = { 30: 4573528164, 60: 4573593700, 90: 4582408292 };
    var raw = (offerSection && offerSection.dataset.sellingPlans) || '';
    raw.split(',').forEach(function (pair) {
      var parts = pair.split(':');
      var days = parseInt(parts[0], 10);
      var id = parseInt(parts[1], 10);
      if (days > 0 && id > 0) sellingPlanMap[days] = id;
    });
    return sellingPlanMap;
  }
  function getSellingPlanId(bagCount) {
    var map = getSellingPlanMap();
    if (bagCount <= 2) return map[30];
    if (bagCount <= 4) return map[60];
    return map[90];
  }

  /* ========================================================================
     DISCOUNT CODE PER QUANTITY
     Match actual totalSelected bags to the tier block with the same quantity
     (1 → tier_1, 2 → tier_2, etc.). Falls back to highest tier for 5+ bags.
     ======================================================================== */
  function getTierElForQuantity(qty) {
    var allTiers = offerSection ? offerSection.querySelectorAll('.sb-funnel__tier') : null;
    if (!allTiers || allTiers.length === 0) return null;
    var exactMatch = null;
    var highestTier = null;
    var highestQty = 0;
    allTiers.forEach(function (t) {
      var tierQty = parseInt(t.dataset.quantity) || 0;
      if (tierQty === qty) exactMatch = t;
      if (tierQty > highestQty) {
        highestQty = tierQty;
        highestTier = t;
      }
    });
    return exactMatch || (qty > highestQty ? highestTier : null);
  }
  function getDiscountCodeForQuantity(qty) {
    var target = getTierElForQuantity(qty);
    return target ? target.dataset.discountCode : null;
  }

  /* ========================================================================
     DOM REFERENCES
     ======================================================================== */
  let offerSection, step1El, step2El, step2Nav;
  let carouselImages, carouselThumbs, carouselDots, prevBtn, nextBtn;
  let tierCards, giftBoxes, giftTitles;
  let step2GiftBoxes, step2GiftTitles;
  let flavorCards;
  let stickyBar, stickyBarCta, stickyBarText;
  let step2Cta, step2CtaText, step2Counter;
  let selectFlavorsCta;

  /* ========================================================================
     PURCHASE MODE (subscription vs try-once)
     'sub'  — classic flow: tier quantity, selling plan, tier discount codes.
     'once' — one-time purchase: min quantity from settings, NO selling plan,
              only the try-once discount code; gifts stay a subscription perk
              unless try_once_gifts is enabled in the section settings.
     ======================================================================== */
  function tryOnceGiftsEnabled() {
    return state.purchaseMode !== 'once' ||
      (offerSection && offerSection.dataset.tryOnceGifts === 'true');
  }
  function setPurchaseMode(mode) {
    state.purchaseMode = mode;
    var once = mode === 'once';
    if (once) {
      state.tierQuantity = parseInt(offerSection.dataset.tryOnceQty) || 1;
    } else {
      var sel = offerSection.querySelector('.sb-funnel__tier--selected');
      if (sel) state.tierQuantity = parseInt(sel.dataset.quantity) || 1;
    }
    offerSection.classList.toggle('sb-funnel--once', once);
    offerSection.classList.toggle('sb-funnel--hide-gifts', !tryOnceGiftsEnabled());
  }

  /* ========================================================================
     EAGER CART CLEAR
     /cart/clear.js measures 2-3s on this store — by far the slowest call in
     the checkout sequence. Fire it when the user ENTERS step 2 so it completes
     in the background while they pick flavors; the checkout CTA then reuses
     the same promise instead of paying that cost on the tap-to-checkout path.
     Trade-off (accepted): entering step 2 wipes any pre-existing cart even if
     the visitor never checks out — fine for a paid-traffic funnel.
     ======================================================================== */
  var cartClearPromise = null;
  function ensureCartCleared() {
    if (!cartClearPromise) {
      cartClearPromise = fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      }).catch(function () { /* non-fatal; add still proceeds */ });
    }
    return cartClearPromise;
  }
  // Once items are added (or an attempt failed midway), the cart is dirty —
  // the next checkout attempt must clear again.
  function invalidateCartClear() { cartClearPromise = null; }

  /* ========================================================================
     STEP SWITCHING
     ======================================================================== */
  function goToStep(stepNum) {
    state.currentStep = stepNum;
    if (stepNum !== 2) setToppingsPhase(false, true);

    // Kick off the slow cart clear as soon as the user commits to step 2.
    if (stepNum === 2) ensureCartCleared();

    if (step1El) step1El.classList.toggle('hidden', stepNum !== 1);
    if (step2El) step2El.classList.toggle('hidden', stepNum !== 2);

    // Dispatch event for other sections to hide/show
    document.dispatchEvent(new CustomEvent('funnel:step-change', {
      detail: { step: stepNum }
    }));

    // Scroll behaviour differs per step. Step 2 is a focused flow, so pin the
    // offer section to the viewport top. Step 1 must scroll to the TRUE page top
    // (0) — scrollIntoView on the offer pins it at the top of the viewport and
    // hides the ~58px header above it (mobile top bar; desktop star-rating row
    // "Excellent 4.8 / 6,000+ happy customers").
    // Solo mode is for pages that put the offer near the bottom. Sending the
    // user to the page top on Back loses their place, so return to the offer.
    var soloStep = !!offerSection && offerSection.dataset.step2Solo === 'true';

    // Release the body scroll lock BEFORE scrolling. Above 1020px the lock puts
    // overflow:hidden and height:100vh on the body. A locked body does not
    // scroll, and releasing it after can restore the old offset.
    if (stepNum !== 2) document.body.classList.remove('sb-funnel-step2-active');

    if (stepNum === 2) {
      if (offerSection) offerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (soloStep && offerSection) {
      // Land on the two columns, not on the lead-in headline and countdown.
      // The user already read those before they picked a pack.
      var backTarget = offerSection.querySelector('.sb-funnel__hero') || offerSection;
      // Jump, do not glide. A smooth scroll here travels the whole page.
      backTarget.scrollIntoView({ block: 'start' });
      // The sections above were display:none. They lay out again now, and a
      // late image can move the offer. Correct once, and stop if the user
      // starts scrolling, so we never fight them.
      var userScrolled = false;
      var markScrolled = function () { userScrolled = true; };
      window.addEventListener('wheel', markScrolled, { passive: true, once: true });
      window.addEventListener('touchmove', markScrolled, { passive: true, once: true });
      setTimeout(function () {
        window.removeEventListener('wheel', markScrolled);
        window.removeEventListener('touchmove', markScrolled);
        if (userScrolled) return;
        if (Math.abs(backTarget.getBoundingClientRect().top) > 2) {
          backTarget.scrollIntoView({ block: 'start' });
        }
      }, 400);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Lock body scroll on step 2, unlock on step 1
    document.body.classList.toggle('sb-funnel-step2-active', stepNum === 2);

    // Ensure position:fixed works on step2 nav by clearing containing block on all ancestors
    if (step2Nav && stepNum === 2) {
      var el = step2Nav.parentElement;
      while (el && el !== document.body) {
        el.style.transform = 'none';
        el.style.willChange = 'auto';
        el.style.filter = 'none';
        el.style.contain = 'none';
        el = el.parentElement;
      }
    }

    // Update step 2 counter
    if (stepNum === 2) {
      updateStep2Counter();
      updateStep2Gifts();
    }

    updateStickyBar();
  }

  /* ========================================================================
     TOPPINGS PHASE
     With a toppings product, the step 2 button first says "Next". The tap
     swaps the flavor list for the toppings list in the same step. Back
     returns to the flavor list. Toppings are optional and one-time only, so
     rebills stay tea only. Nothing here runs without data-toppings-variant.
     ======================================================================== */
  function toppingsOn() {
    return !!offerSection && !!offerSection.dataset.toppingsVariant;
  }
  function setToppingsPhase(on, quiet) {
    on = !!on && toppingsOn();
    if (state.toppingsPhase === on) return;
    state.toppingsPhase = on;
    if (offerSection) offerSection.classList.toggle('sb-funnel--toppings', on);
    if (quiet) return;
    // Start the new list at its top. The right column scrolls on desktop.
    var col = offerSection.querySelector('.sb-funnel__step2-flavors');
    if (col) col.scrollTop = 0;
    offerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    updateStep2Counter();
  }
  function getToppingsCents() {
    if (!toppingsOn() || state.toppings <= 0) return 0;
    return (parseInt(offerSection.dataset.toppingsPrice) || 0) * state.toppings;
  }
  function initToppings() {
    var card = offerSection.querySelector('.sb-funnel__topping');
    if (!card || !toppingsOn()) return;
    var max = parseInt(offerSection.dataset.toppingsMax) || 0;
    var addBtn = card.querySelector('.sb-funnel__add-btn');
    var qtyBox = card.querySelector('.sb-funnel__qty-box');
    var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
    var plusBtn = card.querySelector('.sb-funnel__qty-plus');
    var minusBtn = card.querySelector('.sb-funnel__qty-minus');
    function render() {
      var active = state.toppings > 0;
      if (addBtn) addBtn.style.display = active ? 'none' : '';
      if (qtyBox) qtyBox.classList.toggle('visible', active);
      if (qtyDisplay) qtyDisplay.textContent = String(state.toppings || 1);
      card.classList.toggle('sb-funnel__flavor--active', active);
      if (plusBtn) plusBtn.disabled = max > 0 && state.toppings >= max;
      updateStep2CtaPrices();
      updateStep2AtcInfo();
    }
    function change(delta) {
      var next = Math.max(0, state.toppings + delta);
      if (max > 0) next = Math.min(next, max);
      state.toppings = next;
      render();
    }
    if (addBtn) addBtn.addEventListener('click', function () { change(1); });
    if (plusBtn) plusBtn.addEventListener('click', function () { change(1); });
    if (minusBtn) minusBtn.addEventListener('click', function () { change(-1); });
    card._sbRender = render;
  }
  function resetToppings() {
    state.toppings = 0;
    var card = offerSection && offerSection.querySelector('.sb-funnel__topping');
    if (card && card._sbRender) card._sbRender();
  }

  /* ========================================================================
     IMAGE CAROUSEL (Step 1)
     ======================================================================== */
  function initCarousel() {
    const main = offerSection?.querySelector('.sb-funnel__carousel-main');
    if (!main) return;

    carouselImages = main.querySelectorAll('img');
    state.carouselCount = carouselImages.length;

    carouselThumbs = offerSection.querySelectorAll('.sb-funnel__carousel-thumb');
    carouselDots = offerSection.querySelectorAll('.sb-funnel__carousel-dot');
    prevBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--prev');
    nextBtn = offerSection.querySelector('.sb-funnel__carousel-arrow--next');

    // Thumb clicks
    carouselThumbs.forEach(function (thumb, i) {
      thumb.addEventListener('click', function () { setCarouselIndex(i); });
    });

    // Dot clicks
    carouselDots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { setCarouselIndex(i); });
    });

    // Arrow clicks
    if (prevBtn) prevBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { setCarouselIndex(state.carouselIndex + 1); });

    // Touch swipe on mobile
    let startX = 0;
    main.addEventListener('touchstart', function (e) { startX = e.touches[0].clientX; }, { passive: true });
    main.addEventListener('touchend', function (e) {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        setCarouselIndex(state.carouselIndex + (diff > 0 ? 1 : -1));
      }
    }, { passive: true });

    setCarouselIndex(0);
  }

  function setCarouselIndex(idx) {
    if (idx < 0) idx = 0;
    if (idx >= state.carouselCount) idx = state.carouselCount - 1;
    state.carouselIndex = idx;

    carouselImages.forEach(function (img, i) { img.classList.toggle('active', i === idx); });
    carouselThumbs.forEach(function (t, i) { t.classList.toggle('active', i === idx); });
    carouselDots.forEach(function (d, i) { d.classList.toggle('active', i === idx); });

    if (prevBtn) prevBtn.disabled = idx === 0;
    if (nextBtn) nextBtn.disabled = idx === state.carouselCount - 1;
  }

  /* ========================================================================
     PRICING TIER SELECTION (Step 1)
     ======================================================================== */
  function initTiers() {
    tierCards = offerSection?.querySelectorAll('.sb-funnel__tier:not(.sb-funnel__tier--soldout)');
    if (!tierCards) return;

    tierCards.forEach(function (card, i) {
      card.addEventListener('click', function () {
        // In theme editor, defer DOM mutations so Shopify's block selection runs first
        if (window.Shopify && window.Shopify.designMode) {
          setTimeout(function () { selectTier(card); }, 0);
        } else {
          selectTier(card);
        }
      });
    });

    // Select the configured default tier (from `data-default-tier-qty` on the
    // section root, set via theme editor). Falls back to the first non-soldout
    // tier if the configured qty doesn't match any tier or is sold out.
    var defaultQty = parseInt(offerSection.dataset.defaultTierQty) || 0;
    var defaultTier = null;
    if (defaultQty > 0) {
      tierCards.forEach(function (card) {
        if (!defaultTier && parseInt(card.dataset.quantity) === defaultQty) {
          defaultTier = card;
        }
      });
    }
    var fallbackTier = offerSection.querySelector('.sb-funnel__tier:not(.sb-funnel__tier--soldout)');
    var initialTier = defaultTier || fallbackTier;
    if (initialTier) selectTier(initialTier);
  }

  function selectTier(card) {
    var allTiers = offerSection.querySelectorAll('.sb-funnel__tier');
    allTiers.forEach(function (t) { t.classList.remove('sb-funnel__tier--selected'); });
    card.classList.add('sb-funnel__tier--selected');

    state.tierQuantity = parseInt(card.dataset.quantity) || 1;
    state.selectedTier = parseInt(card.dataset.tierIndex) || 0;

    // Update dynamic CTA details based on selected tier
    var frequency = card.dataset.frequency || '';
    var discount = card.dataset.discount || '';
    var salePrice = card.dataset.salePrice || '';
    var originalTotal = card.dataset.originalTotal || '';
    var qty = state.tierQuantity;

    // Update "Subscribe & Save" label
    var subLabel = offerSection.querySelector('.sb-funnel__sub-label');
    if (subLabel && frequency && discount) {
      subLabel.textContent = 'Subscribe & Save - ' + frequency + ' - ' + discount + ' First Order*';
    }

    // Update CTA button prices
    var priceOriginal = offerSection.querySelector('[data-step="1"] .sb-funnel__cta-price-original');
    var priceSale = offerSection.querySelector('[data-step="1"] .sb-funnel__cta-price-sale');
    if (priceOriginal && originalTotal) priceOriginal.textContent = originalTotal;
    if (priceSale && salePrice) priceSale.textContent = salePrice;

    // Update subscription benefit line — ship cadence based on tier bag count
    // The section owns the words. The script only fills [days] (30 or 60).
    // A template with no placeholder shows as written.
    var benefitLine = offerSection.querySelector('.sb-funnel__sub-benefits-item');
    var benefitText = benefitLine && benefitLine.querySelector('[data-benefit-text]');
    if (benefitText) {
      var shipDays = qty <= 2 ? 30 : 60;
      var tpl = benefitLine.dataset.benefitTemplate || '';
      if (tpl.indexOf('[days]') === -1) {
        // Older templates store "... 30 days" with no placeholder.
        tpl = tpl.replace(/\b(30|60) days\b/, shipDays + ' days');
      }
      benefitText.textContent = tpl.split('[days]').join(String(shipDays));
    }

    updateStep1Gifts();
    updateStickyBar();
  }

  /* ========================================================================
     GIFT TIER PROGRESS
     ======================================================================== */
  function initGifts() {
    giftBoxes = offerSection?.querySelectorAll('[data-step="1"] .sb-funnel__gift');
    giftTitles = offerSection?.querySelectorAll('[data-step="1"] .sb-funnel__gift-title');
    step2GiftBoxes = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift');
    step2GiftTitles = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift-title');
  }

  function updateStep1Gifts() {
    if (!giftBoxes) return;
    giftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var isUnlocked = state.tierQuantity >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', isUnlocked);
    });
    if (giftTitles) {
      giftTitles.forEach(function (title, i) {
        var box = giftBoxes[i];
        if (!box) return;
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        title.classList.toggle('sb-funnel__gift-title--active', state.tierQuantity >= threshold);
      });
    }
  }

  function updateStep2Gifts() {
    if (!step2GiftBoxes) return;
    step2GiftBoxes.forEach(function (box, i) {
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var isUnlocked = state.totalSelected >= threshold;
      box.classList.toggle('sb-funnel__gift--unlocked', isUnlocked);
    });
    if (step2GiftTitles) {
      step2GiftTitles.forEach(function (title, i) {
        var box = step2GiftBoxes[i];
        if (!box) return;
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        title.classList.toggle('sb-funnel__gift-title--active', state.totalSelected >= threshold);
      });
    }
  }

  /* ========================================================================
     FLAVOR QUANTITY CONTROLS (Step 2)
     ======================================================================== */
  function initFlavors() {
    flavorCards = offerSection?.querySelectorAll('.sb-funnel__flavor:not(.sb-funnel__topping)');
    if (!flavorCards) return;

    flavorCards.forEach(function (card) {
      var variantId = card.dataset.variantId;
      var addBtn = card.querySelector('.sb-funnel__add-btn');
      var qtyBox = card.querySelector('.sb-funnel__qty-box');
      var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
      var plusBtn = card.querySelector('.sb-funnel__qty-plus');
      var minusBtn = card.querySelector('.sb-funnel__qty-minus');

      if (addBtn) {
        addBtn.addEventListener('click', function () {
          if (atMaxBags()) return;
          state.flavors[variantId] = 1;
          addBtn.style.display = 'none';
          if (qtyBox) qtyBox.classList.add('visible');
          if (qtyDisplay) qtyDisplay.textContent = '1';
          card.classList.add('sb-funnel__flavor--active');
          recalcTotal();
        });
      }

      if (plusBtn) {
        plusBtn.addEventListener('click', function () {
          if (atMaxBags()) return;
          state.flavors[variantId] = (state.flavors[variantId] || 0) + 1;
          if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          recalcTotal();
        });
      }

      if (minusBtn) {
        minusBtn.addEventListener('click', function () {
          state.flavors[variantId] = (state.flavors[variantId] || 0) - 1;
          if (state.flavors[variantId] <= 0) {
            delete state.flavors[variantId];
            // Reset to Add+ button
            if (addBtn) addBtn.style.display = '';
            if (qtyBox) qtyBox.classList.remove('visible');
            card.classList.remove('sb-funnel__flavor--active');
          } else {
            if (qtyDisplay) qtyDisplay.textContent = state.flavors[variantId];
          }
          recalcTotal();
        });
      }
    });
  }

  function recalcTotal() {
    state.totalSelected = 0;
    for (var key in state.flavors) {
      state.totalSelected += state.flavors[key];
    }
    updateStep2Gifts();
    updateStep2Counter();
    updateStep2AtcInfo();
    updateStickyBar();
    updateMaxCap();
  }

  /* ========================================================================
     STEP 2 CTA PRICES — "Go to Checkout  $137.74  $53.90"
     Exact tier match uses the tier's configured display prices (the numbers
     step 1 promised). Counts past the ladder scale the highest tier's
     per-bag prices. Try-once mode adds up the variant prices of the bags.
     ======================================================================== */
  function parseMoney(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  }

  /* Try-once price ladder from the section setting: "1:40, 2:76, 3:108".
     An exact bag count uses its own price. A count above the last entry uses
     the price per bag of that last entry. No ladder returns null. */
  var tryOnceLadder = null;
  function getTryOnceLadder() {
    if (tryOnceLadder) return tryOnceLadder;
    tryOnceLadder = [];
    var raw = (offerSection && offerSection.dataset.tryOncePrices) || '';
    raw.split(',').forEach(function (part) {
      var pair = part.split(':');
      var qty = parseInt(pair[0]);
      var price = parseFloat(pair[1]);
      if (qty > 0 && !isNaN(price)) tryOnceLadder.push({ qty: qty, price: price });
    });
    tryOnceLadder.sort(function (a, b) { return a.qty - b.qty; });
    return tryOnceLadder;
  }
  /* Try-once discount code ladder: "1:WELCOME1PACK, 2:WELCOME2PACK".
     An exact bag count uses its code. A higher count uses the closest lower
     entry. No ladder falls back to the single try-once code. */
  var tryOnceCodeLadder = null;
  function getTryOnceCode(qty) {
    if (tryOnceCodeLadder === null) {
      tryOnceCodeLadder = [];
      var raw = (offerSection && offerSection.dataset.tryOnceCodes) || '';
      raw.split(',').forEach(function (part) {
        var i = part.indexOf(':');
        if (i < 0) return;
        var q = parseInt(part.slice(0, i));
        var code = part.slice(i + 1).trim();
        if (q > 0 && code) tryOnceCodeLadder.push({ qty: q, code: code });
      });
      tryOnceCodeLadder.sort(function (a, b) { return a.qty - b.qty; });
    }
    var match = null;
    for (var i = 0; i < tryOnceCodeLadder.length; i++) {
      if (tryOnceCodeLadder[i].qty <= qty) match = tryOnceCodeLadder[i];
    }
    if (match) return match.code;
    return (offerSection && offerSection.dataset.tryOnceDiscount) || '';
  }

  /* Bag cap from the max_bags setting. 0 = no cap. */
  function getMaxBags() {
    return parseInt(offerSection && offerSection.dataset.maxBags) || 0;
  }
  function atMaxBags() {
    var max = getMaxBags();
    return max > 0 && state.totalSelected >= max;
  }
  function updateMaxCap() {
    if (!getMaxBags() || !flavorCards) return;
    var full = atMaxBags();
    flavorCards.forEach(function (card) {
      var plus = card.querySelector('.sb-funnel__qty-plus');
      if (plus) plus.disabled = full;
      var add = card.querySelector('.sb-funnel__add-btn');
      if (add && !add.classList.contains('sb-funnel__add-btn--disabled')) add.disabled = full;
    });
  }

  function getTryOncePrice(qty) {
    var ladder = getTryOnceLadder();
    if (!ladder.length || qty <= 0) return null;
    for (var i = 0; i < ladder.length; i++) {
      if (ladder[i].qty === qty) return ladder[i].price;
    }
    var last = ladder[ladder.length - 1];
    if (qty > last.qty) return (last.price / last.qty) * qty;
    return null;
  }
  /* ========================================================================
     PREMIUM FLAVORS — a flavor priced above the base bag (Matcha).
     Every discount on this page is a percentage, so a premium bag gets the
     same percentage as a base bag. The tier texts give that percentage.
     The base bag is the lowest flavor price on the page. Shopify cuts each
     percentage discount down to the cent, so this script does the same.
     The Super Pearls bundle uses fixed dollar tiers, so it gets no premium
     pricing: its button and cards stay as before.
     ======================================================================== */
  var flavorBase = null;
  function getFlavorBase() {
    if (flavorBase) return flavorBase;
    flavorBase = { list: 0, sub: 0 };
    offerSection.querySelectorAll('[data-step="2"] .sb-funnel__flavor:not(.sb-funnel__topping)').forEach(function (card) {
      var list = parseInt(card.dataset.price) || 0;
      var sub = parseInt(card.dataset.subPrice) || 0;
      if (list && (!flavorBase.list || list < flavorBase.list)) flavorBase.list = list;
      if (sub && (!flavorBase.sub || sub < flavorBase.sub)) flavorBase.sub = sub;
    });
    return flavorBase;
  }
  function premiumPricingOn() {
    return !!offerSection && !offerSection.dataset.pearlsVariant;
  }
  /* The share of the start price that a buyer pays at this bag count, and
     the base bag's price at this count, in cents. Subscription bags start
     at the selling plan price. One-time bags start at the product price.
     Returns null when the page gives no price for this count. */
  function getBagPricing(qty) {
    var base = getFlavorBase();
    var perBag;
    var start;
    if (state.purchaseMode === 'once') {
      var onceTotal = getTryOncePrice(qty);
      if (onceTotal == null) return { share: 1, baseBag: base.list, key: 'price' };
      perBag = onceTotal / qty;
      start = base.list;
    } else {
      var tier = getTierElForQuantity(qty);
      var tierQty = tier ? parseInt(tier.dataset.quantity) || 0 : 0;
      var sale = tier ? parseMoney(tier.dataset.salePrice) : null;
      if (sale == null || !tierQty) return null;
      perBag = sale / tierQty;
      start = base.sub;
    }
    if (!start) return null;
    var share = Math.round((perBag * 100 / start) * 1000) / 1000;
    var key = state.purchaseMode === 'once' ? 'price' : 'subPrice';
    return { share: share, baseBag: Math.round(perBag * 100), key: key };
  }
  /* Extra cents one bag of this card costs over a base bag. */
  function getBagPremium(card, pricing) {
    var start = parseInt(card.dataset[pricing.key]) || 0;
    var base = getFlavorBase();
    var baseStart = pricing.key === 'price' ? base.list : base.sub;
    if (!start || start <= baseStart) return { sale: 0, list: 0 };
    var off = Math.floor(start * (1 - pricing.share) + 1e-6);
    return {
      sale: Math.max(0, start - off - pricing.baseBag),
      list: Math.max(0, (parseInt(card.dataset.price) || 0) - base.list)
    };
  }
  /* "+$13.20" on each premium card, for the bag count that prices the box. */
  function updateFlavorPremiums() {
    var labels = offerSection.querySelectorAll('[data-step="2"] [data-flavor-premium]');
    if (!labels.length) return;
    var qty = Math.max(state.totalSelected, state.tierQuantity, 1);
    var pricing = premiumPricingOn() ? getBagPricing(qty) : null;
    labels.forEach(function (label) {
      var card = label.closest('.sb-funnel__flavor');
      var premium = pricing && card ? getBagPremium(card, pricing) : null;
      if (premium && premium.sale > 0) {
        label.textContent = '+$' + (premium.sale / 100).toFixed(2);
        label.hidden = false;
      } else {
        label.textContent = '';
        label.hidden = true;
      }
    });
  }
  /* Extra cents for every premium bag in the box: { sale, list }. */
  function getBoxPremium() {
    var total = { sale: 0, list: 0 };
    if (!premiumPricingOn()) return total;
    var pricing = getBagPricing(state.totalSelected);
    if (!pricing) return total;
    for (var vid in state.flavors) {
      var card = offerSection.querySelector('[data-step="2"] .sb-funnel__flavor[data-variant-id="' + vid + '"]');
      if (!card) continue;
      var premium = getBagPremium(card, pricing);
      total.sale += premium.sale * state.flavors[vid];
      total.list += premium.list * state.flavors[vid];
    }
    return total;
  }

  function updateStep2CtaPrices() {
    updateFlavorPremiums();
    var origEl = offerSection?.querySelector('[data-step2-original]');
    var saleEl = offerSection?.querySelector('[data-step2-sale]');
    if (!origEl || !saleEl) return;

    // The price pill has its own background. Hide the whole pill when there is
    // no price, or the button shows an empty brown pill.
    var pillEl = origEl.closest('.sb-funnel__cta-prices');
    function clear() {
      origEl.textContent = '';
      saleEl.textContent = '';
      if (pillEl) pillEl.hidden = true;
    }
    if (state.totalSelected === 0 || state.totalSelected < state.tierQuantity) { clear(); return; }

    // One-time mode uses the try-once price ladder from the section settings.
    // With no ladder, add up the product prices of the selected bags, but only
    // when no discount code applies at checkout.
    if (state.purchaseMode === 'once') {
      var onceTotal = getTryOncePrice(state.totalSelected);
      // The ladder prices base bags. Premium bags add their extra.
      if (onceTotal != null) onceTotal += getBoxPremium().sale / 100;
      if (onceTotal == null) {
        if (offerSection.dataset.tryOnceDiscount || offerSection.dataset.tryOnceCodes) { clear(); return; }
        onceTotal = 0;
        for (var vid in state.flavors) {
          var card = offerSection.querySelector('[data-step="2"] .sb-funnel__flavor[data-variant-id="' + vid + '"]');
          var cents = card ? parseInt(card.dataset.price) : 0;
          if (!cents) { clear(); return; }
          onceTotal += (cents / 100) * state.flavors[vid];
        }
      }
      onceTotal += getToppingsCents() / 100;
      origEl.textContent = '';
      saleEl.textContent = '$' + onceTotal.toFixed(2);
      if (pillEl) pillEl.hidden = false;
      return;
    }

    var totals = getSubBoxTotals();
    if (!totals) { clear(); return; }
    // Toppings ride at their list price (the tier codes cut tea only).
    var toppingsTotal = getToppingsCents() / 100;
    origEl.textContent = '$' + (totals.orig + toppingsTotal).toFixed(2);
    saleEl.textContent = '$' + (totals.sale + toppingsTotal).toFixed(2);
    if (pillEl) pillEl.hidden = false;
  }

  /* Subscription box totals in dollars: { sale, orig }. The button and the
     savings line both read this, so they always agree. Null when the box
     has no price yet. */
  function getSubBoxTotals() {
    var tier = getTierElForQuantity(state.totalSelected);
    if (!tier) return null;
    var tierQty = parseInt(tier.dataset.quantity) || 0;
    var sale = parseMoney(tier.dataset.salePrice);
    var orig = parseMoney(tier.dataset.originalTotal);
    if (sale == null || orig == null || !tierQty) return null;

    if (state.totalSelected !== tierQty) {
      // beyond the ladder: scale per-bag
      sale = (sale / tierQty) * state.totalSelected;
      orig = (orig / tierQty) * state.totalSelected;
    }
    // The tier texts price base bags. Premium bags add their extra.
    var boxPremium = getBoxPremium();
    sale += boxPremium.sale / 100;
    orig += boxPremium.list / 100;
    return { sale: sale, orig: orig };
  }

  /* ========================================================================
     STEP 2 COUNTER + CTA
     ======================================================================== */
  // The flavor list says "Next" when a toppings phase follows it.
  function readyCtaLabel() {
    if (toppingsOn() && !state.toppingsPhase) {
      return offerSection.dataset.toppingsNext || 'Next';
    }
    return 'Go to Checkout';
  }

  function updateStep2Counter() {
    if (!step2Counter) return;
    step2Counter.textContent = state.toppingsPhase
      ? (offerSection.dataset.toppingsTitle || 'Add your toppings')
      : 'Select at least ' + state.tierQuantity + ' bag' + (state.tierQuantity === 1 ? '' : 's');

    var remaining = state.tierQuantity - state.totalSelected;

    if (step2Cta) {
      if (remaining > 0) {
        step2Cta.disabled = true;
        step2Cta.classList.remove('sb-funnel__cta--ready');
        if (step2CtaText) step2CtaText.textContent = 'Select ' + remaining + ' more bag' + (remaining > 1 ? 's' : '');
      } else {
        step2Cta.disabled = false;
        step2Cta.classList.add('sb-funnel__cta--ready');
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.dataset.processing = 'false';
        if (step2CtaText) step2CtaText.textContent = readyCtaLabel();
      }
    }
    updateStep2CtaPrices();
  }

  /* ========================================================================
     STEP 2 ATC INFO (savings + gifts unlocked)
     ======================================================================== */
  function updateStep2AtcInfo() {
    var savingEl = offerSection?.querySelector('[data-saving]') || document.querySelector('[data-saving]');
    var giftsEl = offerSection?.querySelector('[data-gifts]') || document.querySelector('[data-gifts]');

    // Savings % from the box the button prices: 1 - sale / list total.
    // It rounds to the nearest whole percent, like the tier card texts, so
    // at a tier's own bag count the line matches that tier card.
    // Before the box has a price, it shows the selected tier's discount.
    if (savingEl) {
      if (state.purchaseMode === 'once') {
        savingEl.textContent = 'One-time purchase — no autoship';
      } else {
        var totals = state.totalSelected > 0 ? getSubBoxTotals() : null;
        var pct;
        if (totals && totals.orig > 0) {
          // Same box as the button: toppings count at list price.
          var tpTotal = getToppingsCents() / 100;
          pct = String(Math.max(0, Math.round((1 - (totals.sale + tpTotal) / (totals.orig + tpTotal)) * 100)));
        } else {
          var selectedTier = offerSection?.querySelector('.sb-funnel__tier--selected');
          var discount = selectedTier?.dataset.discount || '0%';
          pct = discount.replace(/[^0-9]/g, '') || '0';
        }
        savingEl.textContent = state.totalSelected > 0 ? "You're saving " + pct + '%' : "You're saving 0%";
      }
    }

    // Count unlocked gifts
    if (giftsEl) {
      var allGifts = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
      var totalGifts = allGifts.length;
      var unlocked = 0;
      allGifts.forEach(function (box) {
        var threshold = parseInt(box.dataset.unlockAt) || 1;
        if (state.totalSelected >= threshold) unlocked++;
      });
      giftsEl.textContent = '✨ GIFTS UNLOCKED: ' + unlocked + '/' + totalGifts;
    }
  }

  /* ========================================================================
     STICKY BOTTOM BAR
     ======================================================================== */
  function updateStickyBar() {
    if (!stickyBar) return;

    if (state.currentStep === 1) {
      // Visibility is controlled by scroll observer, just update text/state
      if (stickyBarText) stickyBarText.textContent = 'Select Flavors';
      if (stickyBarCta) {
        stickyBarCta.disabled = false;
        stickyBarCta.classList.remove('sb-funnel__cta--ready');
      }
    } else {
      // Hide sticky bar in step 2 (step 2 has its own sticky ATC)
      stickyBar.style.display = 'none';
    }
  }

  function initStickyBarObserver() {
    if (!stickyBar || !selectFlavorsCta) return;

    // Hide sticky bar by default
    stickyBar.style.display = 'none';

    // Show sticky bar only when the main "Select Flavors" CTA scrolls out of view
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (state.currentStep !== 1) return;
        if (entry.isIntersecting) {
          // Main CTA is visible — hide sticky bar
          stickyBar.style.display = 'none';
        } else {
          // Main CTA scrolled out of view — show sticky bar
          stickyBar.style.display = '';
        }
      });
    }, { threshold: 0 });

    observer.observe(selectFlavorsCta);
  }

  /* ========================================================================
     DEBUG STATUS PANEL (visible on mobile when ?debug=1 is in URL)
     ======================================================================== */
  function isDebug() {
    try { return new URLSearchParams(window.location.search).get('debug') === '1'; }
    catch (e) { return false; }
  }
  function debugStatus(msg, isError) {
    if (!isDebug()) return;
    var el = document.getElementById('sb-funnel-debug');
    if (!el) {
      el = document.createElement('div');
      el.id = 'sb-funnel-debug';
      el.style.cssText = 'position:fixed;bottom:0;left:0;right:0;background:#111;color:#fff;font:12px/1.3 monospace;padding:8px 12px;z-index:999999;max-height:40vh;overflow:auto;white-space:pre-wrap;word-break:break-all';
      document.body.appendChild(el);
    }
    var line = document.createElement('div');
    line.style.color = isError ? '#ff6b6b' : '#b2f5b2';
    line.textContent = new Date().toLocaleTimeString() + ' ' + msg;
    el.appendChild(line);
    el.scrollTop = el.scrollHeight;
  }

  /* ========================================================================
     CART INTEGRATION
     ======================================================================== */
  function addToCart() {
    var flavorItems = [];
    var sellingPlanId = state.purchaseMode === 'once' ? null : getSellingPlanId(state.totalSelected);
    for (var variantId in state.flavors) {
      if (state.flavors[variantId] > 0) {
        var item = {
          id: parseInt(variantId),
          quantity: state.flavors[variantId]
        };
        if (sellingPlanId) item.selling_plan = sellingPlanId;
        flavorItems.push(item);
      }
    }

    if (flavorItems.length === 0) {
      debugStatus('addToCart: 0 flavor items, aborting', true);
      return;
    }
    // Super Pearls ride with the tea as a required line: one bag at every
    // tier, no selling plan, so rebills stay tea only (same contract as the
    // b1b2 buy box). Present only when the section sets a pearls product.
    var pearlsVariant = offerSection && offerSection.dataset.pearlsVariant;
    if (pearlsVariant) {
      flavorItems.push({ id: parseInt(pearlsVariant, 10), quantity: 1 });
      debugStatus('pearls line added: ' + pearlsVariant);
    }
    // Toppings: optional, one-time, no selling plan.
    if (toppingsOn() && state.toppings > 0) {
      flavorItems.push({ id: parseInt(offerSection.dataset.toppingsVariant, 10), quantity: state.toppings });
      debugStatus('toppings line added: ' + state.toppings);
    }
    debugStatus('addToCart start: ' + flavorItems.length + ' flavor items, sellingPlan=' + sellingPlanId);

    // Collect unlocked gift items separately — they're best-effort.
    // If a gift is sold out, Shopify 422s the whole /cart/add.js. We add
    // flavors first (required), then each gift individually so a sold-out
    // gift silently falls off instead of blocking checkout.
    var giftItems = [];
    var giftBoxes = tryOnceGiftsEnabled()
      ? (offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [])
      : [];
    giftBoxes.forEach(function (box) {
      if (state.purchaseMode === 'once' && box.hasAttribute('data-sub-only')) return;
      var threshold = parseInt(box.dataset.unlockAt) || 1;
      var giftVariant = box.dataset.giftVariant;
      if (giftVariant && state.totalSelected >= threshold) {
        giftItems.push({
          id: parseInt(giftVariant),
          quantity: 1,
          properties: { '_gift': 'true' }
        });
      }
    });
    debugStatus('payload: ' + flavorItems.length + ' flavors + ' + giftItems.length + ' gifts (gifts best-effort)');

    // Disable button during request. Drop --ready so its ::before/::after
    // (shimmer/glow) don't fight the --loading spinner ::after.
    if (step2Cta) {
      step2Cta.disabled = true;
      step2Cta.classList.remove('sb-funnel__cta--ready');
      step2Cta.classList.add('sb-funnel__cta--loading');
    }
    function setStageText(txt) {
      if (step2CtaText) step2CtaText.textContent = txt;
    }
    setStageText('Building your order...');

    // ---- Checkout timing instrumentation -------------------------------
    // Measures each phase of the add->checkout sequence and counts /cart/add
    // 429 retries (Shopify rate-limit / "Verifying your connection" challenge,
    // which trigger the 1200ms backoff below). Always logged to console; also
    // stashed in sessionStorage so it survives the navigation to /checkout —
    // read it there with: JSON.parse(sessionStorage.getItem('sb-funnel-timing'))
    var _t = { start: performance.now(), addRetries: 0, phases: {} };
    function _mark(k) { _t.phases[k] = Math.round(performance.now() - _t.start); }
    function _emitTiming(extra) {
      var payload = {
        slug: funnelSlug,
        phases: _t.phases,
        addRetries: _t.addRetries,
        codes: extra && extra.codes != null ? extra.codes : undefined,
        path: extra && extra.path,
        totalMs: Math.round(performance.now() - _t.start)
      };
      try { console.log('[sb-funnel][timing]', JSON.stringify(payload)); } catch (e) {}
      try { sessionStorage.setItem('sb-funnel-timing', JSON.stringify(payload)); } catch (e) {}
      debugStatus('timing: ' + payload.totalMs + 'ms (addRetries=' + payload.addRetries + ', codes=' + payload.codes + ')');
    }

    // Safety timeout: if nothing navigates in 15s (slow mobile networks,
    // failed redirects), re-enable the button so the user isn't stuck.
    var recoveryTimeout = setTimeout(function () {
      if (step2Cta) {
        step2Cta.disabled = false;
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.classList.add('sb-funnel__cta--ready');
        step2Cta.dataset.processing = 'false';
      }
      setStageText('Go to Checkout');
    }, 15000);
    function clearRecovery() { clearTimeout(recoveryTimeout); }
    function resetCtaState() {
      clearRecovery();
      if (step2Cta) {
        step2Cta.disabled = false;
        step2Cta.classList.remove('sb-funnel__cta--loading');
        step2Cta.classList.add('sb-funnel__cta--ready');
        step2Cta.dataset.processing = 'false';
      }
      // Restore the label — otherwise it stays stuck on the stage text
      // ("Building your order...") after a failed attempt.
      setStageText('Go to Checkout');
    }

    // /cart/add.js can return 429 — either third-party cart scripts hammering
    // /cart.json, Shopify rate-limiting a request burst, or Shopify's
    // "Verifying your connection" bot challenge. A transient 429 usually clears
    // within a few hundred ms, so retry with EXPONENTIAL BACKOFF + JITTER rather
    // than one long flat wait: ~400ms → ~800ms → ~1600ms (up to 3 retries).
    // This recovers ~800ms faster than the old flat 1200ms in the common case
    // (first retry succeeds) and gives more chances before checkout hard-fails.
    // Jitter de-syncs concurrent retries so we don't re-trip the limit in lockstep.
    var ADD_MAX_ATTEMPTS = 4; // 1 initial attempt + 3 retries
    function addBatch(items, attempt) {
      attempt = attempt || 1;
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (res) {
        if (res.ok) return res.json();
        if (res.status === 429 && attempt < ADD_MAX_ATTEMPTS) {
          _t.addRetries++;
          var base = 400 * Math.pow(2, attempt - 1);           // 400, 800, 1600
          var delay = Math.round(base * (0.75 + Math.random() * 0.5)); // ±25% jitter
          debugStatus('429 on /cart/add — retry ' + attempt + '/' + (ADD_MAX_ATTEMPTS - 1) + ' in ' + delay + 'ms', true);
          return new Promise(function (resolve) {
            setTimeout(function () { resolve(addBatch(items, attempt + 1)); }, delay);
          });
        }
        return res.text().then(function (body) {
          var err = new Error('HTTP ' + res.status + ': ' + body.slice(0, 200));
          err.status = res.status;
          err.body = body;
          throw err;
        });
      });
    }
    // Fast path: try flavors + gifts in ONE cart/add.js call (1 round-trip).
    // If Shopify rejects (usually because a gift is sold out — 422), fall back
    // to flavors alone + individual parallel gift adds.
    function addAllItems() {
      invalidateCartClear(); // items are about to land — next attempt must re-clear
      var combined = flavorItems.concat(giftItems);
      debugStatus('POST /cart/add.js (combined: ' + combined.length + ' items)');
      return addBatch(combined, 1).then(function (data) {
        debugStatus('combined add OK (1 round-trip)');
        return data;
      }).catch(function (err) {
        debugStatus('combined add failed, fallback: ' + (err.message || '').slice(0, 80), true);
        // Flavors required, gifts best-effort in parallel.
        return addBatch(flavorItems, 1).then(function (data) {
          debugStatus('flavors OK');
          if (giftItems.length === 0) return data;
          return Promise.all(giftItems.map(function (gift) {
            return addBatch([gift], 1).then(function () {
              debugStatus('gift added: ' + gift.id);
            }).catch(function (e) {
              debugStatus('gift skipped (' + gift.id + '): ' + (e.message || '').slice(0, 80), true);
            });
          })).then(function () { return data; });
        });
      });
    }

    // Cart attributes for order tagging via Shopify Flow.
    // Flow rule: if cart.attributes["multi-step-lp"] exists, tag order with
    // `lander:<slug>` and `tier:<qty>`.
    var funnelSlug = offerSection && offerSection.dataset.funnelSlug
      ? offerSection.dataset.funnelSlug
      : 'mt-ms-01';
    var offerSource = offerSection && offerSection.dataset.offerSource
      ? offerSection.dataset.offerSource
      : 'paid-offer-bundle';
    function setCartAttributes() {
      var attrs = {
        tier: String(state.totalSelected),
        source: offerSource,
        purchase_type: state.purchaseMode === 'once' ? 'one-time' : 'subscription',
        // Clear a template tag left by an earlier pdp-v4 visit. /cart/clear.js
        // empties line items only, and /cart/update.js writes the keys it sends,
        // so template = pdp-v4 survives into this cart. The Pricing Studio
        // discount function then prices the cart flat (FIRSTORDEROFFER, $29.00)
        // instead of the tier code this page applies ($29.92). An empty string
        // removes a cart attribute.
        template: ''
      };
      attrs['multi-step-lp'] = funnelSlug;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () { /* non-fatal */ });
    }

    // Shared discount-code collection: tier code (picked by state.totalSelected
    // so adding bags auto-bumps tier), then per-gift unlock codes, then the
    // global gift discount. Dedupes via `seen`.
    function collectCodes() {
      var codes = [];
      var seen = {};
      function pushCode(c) {
        if (!c) return;
        var trimmed = c.trim();
        if (!trimmed || seen[trimmed]) return;
        seen[trimmed] = true;
        codes.push(trimmed);
      }
      if (state.purchaseMode === 'once') {
        pushCode(getTryOnceCode(state.totalSelected));
      } else {
        pushCode(getDiscountCodeForQuantity(state.totalSelected));
      }
      if (tryOnceGiftsEnabled()) {
        var step2Gifts = offerSection?.querySelectorAll('[data-step="2"] .sb-funnel__gift') || [];
        step2Gifts.forEach(function (box) {
          if (state.purchaseMode === 'once' && box.hasAttribute('data-sub-only')) return;
          var threshold = parseInt(box.dataset.unlockAt) || 1;
          if (state.totalSelected >= threshold) pushCode(box.dataset.giftDiscountCode);
        });
        pushCode(offerSection?.dataset.giftDiscount);
      }
      return codes;
    }

    function handleCheckoutError(err) {
      console.error('Cart add failed:', err);
      debugStatus('EXCEPTION: ' + (err && err.message ? err.message : String(err)), true);
      resetCtaState();
    }

    // Checkout flow:
    //   1. /cart/clear.js
    //   2. /cart/add.js + /cart/update.js (attributes) — IN PARALLEL (saves 1 RTT)
    //   3. Pre-fetch all-but-last discount codes via /discount/CODE individually
    //      (each call saves the code to the cart session). Final redirect via
    //      /discount/LAST_CODE?redirect=/checkout applies the last one and goes
    //      to checkout. This is the only reliable way to stack codes in Shopify
    //      — `/discount/CODE1,CODE2` is NOT supported (Shopify treats the whole
    //      comma-string as one code lookup, so NO discount applies).
    debugStatus('checkout: ' + funnelSlug);
    console.log('[sb-funnel] checkout flow start, slug=' + funnelSlug);
    // Reuse the clear fired at step-2 entry (usually already resolved by now);
    // only a retry after a failed attempt pays for a fresh clear here.
    ensureCartCleared()
    .then(function () {
      _mark('clear');
      setStageText('Building your order...');
      // cart/add + cart/update in parallel — saves one round-trip vs sequential.
      return Promise.all([addAllItems(), setCartAttributes()]);
    })
    .then(function () {
      _mark('addUpdate');
      setStageText('Activating your savings...');
      var codes = collectCodes();
      console.log('[sb-funnel] codes:', codes, 'totalSelected=' + state.totalSelected);

      if (codes.length === 0) {
        debugStatus('no codes → /checkout');
        console.log('[sb-funnel] → /checkout (no codes)');
        _emitTiming({ codes: 0, path: 'no-codes' });
        clearRecovery();
        window.location.href = '/checkout';
        return;
      }

      // Apply ALL codes in a SINGLE navigation via the /checkout?discount= query
      // param (comma-separated). This is what high-velocity offer pages (e.g.
      // mycarpe.com) do, and it replaces the old per-code /discount/CODE prefetch
      // chain — removing a full round-trip AND shrinking the request burst, which
      // also lowers the chance of tripping Shopify's edge bot challenge on /cart.
      //
      // IMPORTANT: the comma syntax only works on the ?discount= QUERY param, NOT
      // the /discount/CODE1,CODE2 PATH form (Shopify treats that whole string as
      // one code lookup). Stacking requires the codes be configured combinable —
      // verified via Admin API: tier codes + FREEGIFT all combinesWith
      // order/product/shipping = true.
      clearRecovery();
      var nav = '/checkout?discount=' + codes.map(encodeURIComponent).join(',');
      debugStatus('navigate → ' + nav);
      console.log('[sb-funnel] navigate →', nav);
      _emitTiming({ codes: codes.length, path: 'checkout-discount-param' });
      window.location.href = nav;
    })
    .catch(handleCheckoutError);
  }

  /* ========================================================================
     CONTENT SECTION VISIBILITY
     ======================================================================== */
  document.addEventListener('funnel:step-change', function (e) {
    // Hide all sections between the offer and sticky bar when on step 2
    // Target by section class names AND data attribute
    var selectors = [
      '.sb-lp-funnel-benefits-section',
      '.sb-lp-funnel-social-section',
      '.sb-lp-funnel-sticky-bar-section',
      '[data-funnel-content]'
    ];

    // Solo mode hides the sections ABOVE the offer as well, so step 2 shows
    // only the offer. Pages that put the offer near the top do not need it.
    // Pages that put the offer near the bottom do.
    var soloEl = document.getElementById('sb-funnel-offer');
    var solo = !!soloEl && soloEl.dataset.step2Solo === 'true';

    // Also find any shopify-section that wraps our reused sections
    var allSections = document.querySelectorAll('.shopify-section');
    allSections.forEach(function (section) {
      // Skip the offer section itself
      if (section.querySelector('#sb-funnel-offer')) return;
      // Skip if it's the sticky bar
      if (section.querySelector('.sb-funnel-sticky')) {
        if (e.detail.step === 2) {
          section.classList.add('sb-funnel-hidden');
        } else {
          section.classList.remove('sb-funnel-hidden');
        }
        return;
      }
      // Check if this section is between offer and sticky
      var offerEl = document.getElementById('sb-funnel-offer');
      if (!offerEl) return;
      var offerSectionEl = offerEl.closest('.shopify-section');
      if (!offerSectionEl) return;

      // Compare DOM order
      var follows = offerSectionEl.compareDocumentPosition(section) & Node.DOCUMENT_POSITION_FOLLOWING;
      if (follows || solo) {
        if (e.detail.step === 2) {
          section.classList.add('sb-funnel-hidden');
        } else {
          section.classList.remove('sb-funnel-hidden');
        }
      }
    });
  });

  /* ========================================================================
     COUNTDOWN TIMER
     ======================================================================== */
  function initCountdown() {
    var timerEl = document.querySelector('[data-funnel-countdown]');
    if (!timerEl) return;

    var endStr = timerEl.dataset.funnelCountdown;
    var endDate;

    if (endStr) {
      endDate = new Date(endStr);
    } else {
      // Rolling 3-day countdown
      endDate = new Date();
      endDate.setDate(endDate.getDate() + 3);
      endDate.setHours(23, 59, 59, 0);
    }

    var hrsEl = timerEl.querySelector('[data-hours]');
    var minEl = timerEl.querySelector('[data-minutes]');
    var secEl = timerEl.querySelector('[data-seconds]');

    function tick() {
      var now = Date.now();
      var diff = endDate.getTime() - now;
      if (diff <= 0) diff = 0;

      var hrs = Math.floor(diff / 3600000);
      var mins = Math.floor((diff % 3600000) / 60000);
      var secs = Math.floor((diff % 60000) / 1000);

      if (hrsEl) hrsEl.textContent = String(hrs).padStart(2, '0');
      if (minEl) minEl.textContent = String(mins).padStart(2, '0');
      if (secEl) secEl.textContent = String(secs).padStart(2, '0');
    }

    tick();
    setInterval(tick, 1000);
  }

  /* ========================================================================
     INIT
     ======================================================================== */
  function init() {
    offerSection = document.getElementById('sb-funnel-offer');
    if (!offerSection) return;

    step1El = offerSection.querySelector('[data-step="1"]');
    step2El = offerSection.querySelector('[data-step="2"]');

    // Step 1 elements
    selectFlavorsCta = offerSection.querySelector('[data-action="select-flavors"]');
    if (selectFlavorsCta) {
      selectFlavorsCta.addEventListener('click', function () {
        setPurchaseMode('sub');
        goToStep(2);
      });
    }

    var tryOnceCta = offerSection.querySelector('[data-action="try-once"]');
    if (tryOnceCta) {
      tryOnceCta.addEventListener('click', function () {
        setPurchaseMode('once');
        goToStep(2);
      });
    }

    // Step 2 elements
    step2Nav = offerSection.querySelector('.sb-funnel__step2-nav');
    var backBtn = offerSection.querySelector('.sb-funnel__back-btn');
    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (state.toppingsPhase) { setToppingsPhase(false); return; }
        goToStep(1);
      });
    }

    step2Cta = offerSection.querySelector('[data-action="checkout"]');
    step2CtaText = step2Cta?.querySelector('.sb-funnel__cta-text');
    step2Counter = offerSection.querySelector('.sb-funnel__step2-title');

    if (step2Cta) {
      step2Cta.addEventListener('click', function () {
        if (step2Cta.disabled) return;
        // Prevent double-submit from rapid taps on mobile
        if (step2Cta.dataset.processing === 'true') return;
        // First tap with a toppings step: show the toppings list.
        if (toppingsOn() && !state.toppingsPhase) {
          setToppingsPhase(true);
          return;
        }
        step2Cta.dataset.processing = 'true';
        addToCart();
      });
    }

    // Sticky bar
    stickyBar = document.querySelector('.sb-funnel-sticky');
    stickyBarCta = stickyBar?.querySelector('.sb-funnel__cta');
    stickyBarText = stickyBar?.querySelector('.sb-funnel__cta-text');

    if (stickyBarCta) {
      stickyBarCta.addEventListener('click', function () {
        if (state.currentStep !== 1) return;
        // Scroll back to the quantity selector so the user can review tiers,
        // then hit the main CTA to advance.
        var tiersEl = offerSection?.querySelector('.sb-funnel__tiers');
        (tiersEl || offerSection)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // Initialize components
    initCarousel();
    initGifts();
    initTiers();
    initFlavors();
    initToppings();
    initCountdown();
    initStickyBarObserver();
    updateStickyBar();
  }

  /* ========================================================================
     RESET ON RETURN — if the user hits back from checkout, reset to step 1
     with no flavors selected so they can't accidentally reuse stale state
     ======================================================================== */
  function resetFunnelState() {
    state.flavors = {};
    state.totalSelected = 0;
    resetToppings();
    invalidateCartClear();
    setPurchaseMode('sub');
    if (flavorCards) {
      flavorCards.forEach(function (card) {
        card.classList.remove('sb-funnel__flavor--active');
        var addBtn = card.querySelector('.sb-funnel__add-btn');
        var qtyBox = card.querySelector('.sb-funnel__qty-box');
        var qtyDisplay = card.querySelector('.sb-funnel__qty-display');
        if (addBtn) addBtn.style.display = '';
        if (qtyBox) qtyBox.classList.remove('visible');
        if (qtyDisplay) qtyDisplay.textContent = '1';
      });
    }
    if (step2Cta) {
      step2Cta.disabled = false;
      step2Cta.classList.remove('sb-funnel__cta--loading');
      step2Cta.dataset.processing = 'false';
    }
    goToStep(1);
  }

  // Reset ONLY on bfcache restore (event.persisted === true) — i.e. the user
  // hit browser-back from checkout and the page was restored from cache showing
  // stale step-2 state. Do NOT reset on a fresh load: pageshow fires AFTER the
  // `load` event, so on a heavy LP it can fire 1-2s in — and if the visitor
  // already tapped "Select Flavors" during that window, an unconditional reset
  // bounces them back to step 1. A fresh load already starts on step 1 anyway.
  window.addEventListener('pageshow', function (event) {
    if (!offerSection) return;
    if (event.persisted) resetFunnelState();
  });

  // Boot
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
